/**
 * Prise en main de la feuille de brouillon, dans le salon d'un défi live privé.
 *
 * Un invité, ou un joueur qui n'a jamais pris le brouillon en main, découvre comment il marche AVANT le départ :
 * pendant le jeu, le chrono tourne. C'est le didacticiel guidé (l'exercice de démo de la classe du défi, coach qui
 * montre où toucher), en surimpression : le salon reste ouvert dessous, et si le défi démarre pendant ce temps, on
 * arrive directement dans le jeu. Il s'ouvre tout seul à la première prise en main (jamais pour l'organisateur, qui a
 * le défi à lancer) ; ensuite la carte permet de le refaire. Rien n'est enregistré, aucun HORA.
 */

import { Suspense, lazy, useCallback, useState } from 'react';
import { PencilLine } from 'lucide-react';
import { Button, Card } from '@jeraisonne/ui';

// le didacticiel charge le moteur v2 : seulement si on l'ouvre
const GuidedTutorialOverlay = lazy(() =>
  import('@/features/scratch-tutorial/guided-tutorial').then((m) => ({
    default: m.GuidedTutorialOverlay,
  })),
);

export function ScratchPracticeCard({
  level,
  autoOpen = false,
}: {
  level?: string | null;
  /** première prise en main : le didacticiel s'ouvre tout seul (une fois) */
  autoOpen?: boolean;
}) {
  const [open, setOpen] = useState(autoOpen);
  const close = useCallback(() => setOpen(false), []);
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="scratch-practice">
      <div className="flex items-start gap-3">
        <span className="bg-primary-50 text-primary-600 flex h-10 w-10 flex-none items-center justify-center rounded-xl">
          <PencilLine className="h-5 w-5" aria-hidden />
        </span>
        <div className="space-y-1">
          <p className="font-display text-ink-900 font-bold">Découvre la feuille de brouillon</p>
          <p className="text-ink-600 text-sm">
            Ce défi a des questions à résoudre sur la feuille. Essaie-la maintenant (1 minute) :
            pendant le jeu, le chrono tourne.
          </p>
        </div>
      </div>
      <Button variant="outline" fullWidth className="min-h-11" onClick={() => setOpen(true)}>
        Essayer la feuille
      </Button>
      {open && (
        <Suspense fallback={null}>
          <GuidedTutorialOverlay
            level={level}
            context="defi"
            intro="Ce défi a des questions à résoudre sur la feuille."
            doneLabel="Revenir au défi"
            skipLabel="Revenir au défi"
            onDone={close}
          />
        </Suspense>
      )}
    </Card>
  );
}
